<svelte:options
  customElement={{
    tag: "oc-back-to-top-button-v1",
    extend: window.__components.extend({ asyncInit: true }),
    props: {},
  }}
/>

<script lang="ts">
  import { currentBreakpoint } from "@otto-ec/global-resources/viewport";
  import { onDestroy, onMount } from "svelte";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import { selector } from "./config";
  import { nextVisibility, SCROLLED_UP_THRESHOLD } from "./scroll-state";
  import { trackClicked, trackShownOnce } from "./tracking";

  let mainContainer = $state<HTMLElement>();
  let buttonElement = $state<HTMLElement>();
  let visible = $state(false);

  let resizeObserver: ResizeObserver | null = null;
  let animationFrame: number | null = null;
  let previousY = 0;
  let scrollReferenceY = 0;

  const outerSpacingX = 24;
  const bottomTolerance = 20;
  const isExtraLarge = $derived($currentBreakpoint === "xl");

  function setVisible(nextVisible: boolean): void {
    if (visible === nextVisible) return;

    visible = nextVisible;
    if (nextVisible) trackShownOnce();
  }

  function evaluate(): void {
    const currentY = isExtraLarge
      ? window.scrollY || document.documentElement.scrollTop
      : Math.max(0, window.scrollY);
    const isAtBottom =
      window.innerHeight + currentY >= document.documentElement.scrollHeight - bottomTolerance;
    setVisible(
      nextVisibility({
        currentY,
        previousY,
        scrollReferenceY,
        viewportHeight: window.innerHeight,
        isExtraLarge,
        isAtBottom,
        isVisible: visible,
      }),
    );
    if (
      isExtraLarge ||
      currentY > previousY ||
      currentY < scrollReferenceY - SCROLLED_UP_THRESHOLD
    ) {
      scrollReferenceY = currentY;
    }
    previousY = currentY;
  }

  function updateDynamicLeft(): void {
    if (!buttonElement || !mainContainer) return;

    if (!isExtraLarge) {
      /*                                                          */
      buttonElement.style.removeProperty("--back-to-top-dynamic-left");
      return;
    }

    const left = mainContainer.getBoundingClientRect().right + outerSpacingX;
    buttonElement.style.setProperty("--back-to-top-dynamic-left", `${left}px`);
  }

  function onScroll(): void {
    if (animationFrame === null) {
      animationFrame = requestAnimationFrame(() => {
        evaluate();
        /*                                                                            */
        if (isExtraLarge) updateDynamicLeft();
        animationFrame = null;
      });
    }
  }

  function scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    });

    trackClicked();
  }

  function handleLayoutChange(): void {
    visible = false;
    updateDynamicLeft();
  }
  /*                                               */
  $effect(handleLayoutChange);

  function setupObservers(): void {
    mainContainer = document.querySelector(selector) as HTMLElement;
    if (!mainContainer) return;

    resizeObserver = new ResizeObserver(handleLayoutChange);
    resizeObserver.observe(mainContainer);
    window.addEventListener("resize", handleLayoutChange, { passive: true });
    updateDynamicLeft();
  }

  onMount(() => {
    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    setupObservers();

    return () => {
      if (resizeObserver && mainContainer) {
        resizeObserver.unobserve(mainContainer);
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", handleLayoutChange);
    };
  });

  onDestroy(() => {
    window.removeEventListener("scroll", onScroll);
    if (animationFrame !== null) cancelAnimationFrame(animationFrame);
  });
</script>

<oc-icon-button-v3
  bind:this={buttonElement}
  variant="secondary-on-color"
  elevation-level="sticky"
  size={/*                      */ "100"}
  icon="arrow-long-up"
  oc-aria-label="Nach oben scrollen"
  onclick={scrollToTop}
  class:is-visible={visible}
  tabindex="-1"
  aria-hidden={!visible}
  inert={!visible}
>
</oc-icon-button-v3>

<oc-popover-v1 variant="tooltip" position="top" anchor={buttonElement}>
  Nach oben scrollen
</oc-popover-v1>

<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  :global {
    oc-icon-button-v3 {
      opacity: 0;
      pointer-events: none;
      position: fixed;
      left: 50%; /*                                             */
      top: tokens.$oc-component-back-to-top-button-outer-spacing-y;
      transform: translateX(-50%);
      transition: opacity tokens.$oc-component-back-to-top-button-appear-duration
        tokens.$oc-component-back-to-top-button-appear-easing;
      z-index: 3000; /*                                         */

      &.is-visible {
        opacity: 1;
        pointer-events: auto;
      }
    }

    @include mixins.breakpoint-from-s(false, false) {
      oc-icon-button-v3 {
        left: 50%;
      }
    }

    @include mixins.breakpoint-from-l(false, false) {
      oc-icon-button-v3 {
        left: auto;
        right: tokens.$oc-component-back-to-top-button-outer-spacing-x;
        top: tokens.$oc-component-back-to-top-button-outer-spacing-y;
        transform: none;
      }
    }

    @include mixins.breakpoint-from-xxl(false) {
      oc-icon-button-v3 {
        bottom: tokens.$oc-component-back-to-top-button-outer-spacing-y;
        left: var(--back-to-top-dynamic-left, auto);
        right: auto;
        top: auto;
      }
    }
  }
</style>
